import { PlaySquareTwoTone, SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Form, Input, Space, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { useDispatch } from 'react-redux';
import { useContentCards, useContentsState } from 'src/features/contents/contents.hooks';
import { createContent, fetchContents } from 'src/features/contents/contents.state';
import tileStyles from 'src/styles/tile.module.css';
import { AppDispatch } from 'src/index';
import { useForm } from 'antd/es/form/Form';
import { ContentSource, NewContentCard, VRFormat, VRType } from 'src/features/contents/contents.types';
import { useDebounce } from 'react-use';
import { FormattedMessage, useIntl } from 'react-intl';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';

const emptyEntity: NewContentCard = {
  name: '',
  vrType: VRType.VR180,
  vrFormat: VRFormat.twoD,
  source: ContentSource.localStorage,
  uri: 'file://localstorage',
};

const ContentCardsPage: React.FC = () => {
  const contents = useContentCards();
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const contentsState = useContentsState();

  const [createMode, setCreateMode] = useState<boolean>(false);
  const [newEntity, setNewEntity] = useState<NewContentCard>(emptyEntity);
  const [form] = useForm();
  const [name, setName] = useState<string | undefined>(undefined);

  const handleFormSubmit = useCallback(async () => {
    const response = await dispatch(createContent(newEntity));

    if (createContent.fulfilled.match(response)) {
      history.push(`/content/cards/${response.payload.id}`);
    }
  }, [dispatch, history, newEntity]);
  useDebounce(
    () => {
      if (name !== undefined) {
        dispatch(fetchContents({ params: { name } }));
      }
    },
    300,
    [dispatch, name]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/content/cards'>
                <Space>
                  <PlaySquareTwoTone />
                  <FormattedMessage id={'view.content.cards.title'} />
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            <FormattedMessage id={'view.content.cards.title'} />
          </Typography.Title>
          <Input
            autoFocus
            allowClear
            placeholder={intl.formatMessage({ id: 'view.content.templates.input.search.placeholder' })}
            prefix={<SearchOutlined />}
            onChange={e => {
              setName(e.target.value);
            }}
            style={{ marginBottom: 10 }}
          />
          <div className={styles.content}>
            <LoaderWrapper loading={contentsState.isPending}>
              {createMode && (
                <Form
                  form={form}
                  validateTrigger='onBlur'
                  layout='vertical'
                  className={tileStyles.listTile}
                  onFinish={handleFormSubmit}
                >
                  <Form.Item name='name' noStyle>
                    <Input
                      autoFocus
                      type='text'
                      name='name'
                      placeholder='Введите название карточки'
                      onChange={ev => setNewEntity(e => ({ ...e, name: ev.target.value }))}
                    />
                  </Form.Item>
                  <Button style={{ marginLeft: 10 }} htmlType='submit' type='primary' disabled={newEntity.name === ''}>
                    Создать
                  </Button>
                  <Button
                    style={{ marginLeft: 10 }}
                    htmlType='button'
                    type='default'
                    onClick={() => setCreateMode(false)}
                  >
                    Отменить
                  </Button>
                </Form>
              )}
              {contents.map(c => (
                <div key={c.id} className={tileStyles.listTile}>
                  <Link to={`/content/cards/${c.id}/details`} className={tileStyles.tileName}>
                    <Space>
                      <PlaySquareTwoTone />
                      {c.name}
                      {c.free && <Tag>Бесплатно</Tag>}
                    </Space>
                  </Link>
                  <Space>
                    {c.disabled && <Tag>Заблокирована</Tag>}
                    {c.archived && <Tag color={'warning'}>Архивная</Tag>}
                    {c.devices && c.devices.map(d => <Tag key={d}>{d}</Tag>)}
                  </Space>
                </div>
              ))}
            </LoaderWrapper>
          </div>
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button type='primary' onClick={() => setCreateMode(true)}>
              Создать новую карточку
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const ContentCardsPageBlock: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchContents({}));
  }, [dispatch]);

  return <ContentCardsPage />;
};

export default ContentCardsPageBlock;
